// pages/creator-panel/AuthShell.js
// The shared chrome behind the auth pages — the same split layout, branding
// panel, palette and field styling the Sign In page uses, so Forgot Password
// and Set New Password stop looking like a different product.
//
// SignIn.js still carries its own copy of this markup; it is the critical
// login path and was left untouched. It can adopt this shell later.
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LanguageIcon from '@mui/icons-material/Language';
import SchoolIcon from '@mui/icons-material/School';
import brandingBg from '../../assets/images/bot-avatar.jpg';

export const colors = {
    sidebarBg: '#1e293b',
    sidebarHover: '#313d4a',
    sidebarText: '#dee4ee',
    sidebarTextMuted: '#8a99af',
    accent: '#3b82f6',
    bgPrimary: '#f8fafc',
    border: '#e2e8f0',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    success: '#059669',
    danger: '#dc2626',
};

export const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

export const KlonITLogo = ({ color = 'white', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
        <path d="M8 6L16 16L8 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 6L24 16L16 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
    </svg>
);

// Borderless, translucent fields — matches the Sign In inputs exactly.
export const makeInputSx = (theme) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px', fontFamily, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
        border: 'none', outline: 'none',
        '& .MuiOutlinedInput-notchedOutline': { display: 'none !important' },
        '& fieldset': { display: 'none !important' },
        '&.Mui-focused': { bgcolor: 'rgba(255,255,255,1)', boxShadow: `0 0 0 2px ${theme.palette.primary.main}40` },
        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
    },
    '& .MuiInputBase-input': {
        fontFamily, fontSize: '0.875rem', py: '0.75rem', px: '0.5rem',
        border: 'none', outline: 'none',
        '&:focus': { outline: 'none', boxShadow: 'none' },
        '&::placeholder': { color: colors.textTertiary, opacity: 1 },
    },
    '& .MuiInputAdornment-root': { color: colors.textTertiary },
    '& .MuiFormHelperText-root': { fontFamily, fontSize: '0.6875rem', color: colors.textTertiary, ml: 1.5 },
});

export const makePrimaryButtonSx = (theme) => ({
    py: '0.75rem', fontFamily, fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: '#fff', borderRadius: '14px', height: 46, gap: 1,
    boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
    transition: 'all 0.25s ease',
    '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
        transform: 'translateY(-1px)',
    },
    '&:disabled': { opacity: 0.7 },
});

export const alertSx = {
    mb: 1.5, borderRadius: '12px', fontFamily, fontSize: '0.8125rem',
    '& .MuiAlert-message': { fontFamily },
};

const FeatureCard = ({ icon, title, description }) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        p: '1rem 1.25rem', bgcolor: colors.sidebarHover,
        border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
        transition: 'all 0.25s ease',
        '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.2)', transform: 'translateX(4px)' },
    }}>
        <Box sx={{
            width: 42, height: 42, bgcolor: 'rgba(59,130,246,0.15)', borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: colors.accent,
        }}>
            {icon}
        </Box>
        <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.sidebarText, mb: 0.25, fontFamily }}>{title}</Typography>
            <Typography sx={{ fontSize: '0.875rem', color: colors.sidebarTextMuted, lineHeight: 1.5, fontFamily }}>{description}</Typography>
        </Box>
    </Box>
);

/**
 * Split auth layout: branding on the left (hidden below lg), a centred card
 * on the right with an icon, a title and whatever the page needs.
 */
export default function AuthShell({ icon, title, subtitle, children, footer }) {
    const theme = useTheme();

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');`}</style>

            <Box sx={{ fontFamily, minHeight: '100vh', bgcolor: colors.bgPrimary, display: 'flex' }}>
                {/* Left branding panel */}
                <Box sx={{
                    display: { xs: 'none', lg: 'flex' }, width: '50%',
                    flexDirection: 'column', justifyContent: 'center', p: '4rem', position: 'relative', overflow: 'hidden',
                    backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.9)), url(${brandingBg})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    '&::before': {
                        content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                        background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.08) 0%, transparent 40%)',
                    },
                }}>
                    <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 480, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.875rem', mb: 3 }}>
                            <Box sx={{
                                width: 48, height: 48, bgcolor: colors.accent, borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                            }}>
                                <KlonITLogo />
                            </Box>
                            <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.025em', fontFamily }}>
                                KlonIT
                            </Typography>
                        </Box>

                        <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 2, py: 0.5, bgcolor: colors.sidebarHover,
                            border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px',
                            fontSize: '0.8125rem', fontWeight: 600, color: colors.accent, mb: 2, letterSpacing: '0.05em',
                        }}>
                            <BoltIcon sx={{ fontSize: 14 }} />
                            CLONE · SCALE · GROW
                        </Box>

                        <Typography sx={{ fontSize: '2.75rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em', color: '#fff', mb: 1.5, fontFamily }}>
                            AI-Powered<br /><Box component="span" sx={{ color: colors.accent }}>Business Growth</Box>
                        </Typography>

                        <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.sidebarTextMuted, mb: 2.5, fontFamily }}>
                            Deploy intelligent AI agents that engage customers 24/7, automate workflows, and scale your business across 52+ languages.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                            <FeatureCard icon={<ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />} title="24/7 AI Agents" description="Engage customers on web, WhatsApp & email" />
                            <FeatureCard icon={<LanguageIcon sx={{ fontSize: 20 }} />} title="52+ Languages" description="Scale globally with native language support" />
                            <FeatureCard icon={<SchoolIcon sx={{ fontSize: 20 }} />} title="Student Learning" description="Interactive lessons, tutoring & progress tracking" />
                        </Box>
                    </Box>
                </Box>

                {/* Right form panel */}
                <Box sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    p: { xs: 2, lg: '3rem 4rem' }, position: 'relative', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)',
                    '&::before': {
                        content: '""', position: 'absolute', top: '-30%', right: '-20%',
                        width: '500px', height: '500px', borderRadius: '50%', pointerEvents: 'none',
                        background: `radial-gradient(circle, ${theme.palette.primary.main}12 0%, transparent 70%)`,
                    },
                    '&::after': {
                        content: '""', position: 'absolute', bottom: '-20%', left: '-10%',
                        width: '400px', height: '400px', borderRadius: '50%', pointerEvents: 'none',
                        background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
                    },
                }}>
                    <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
                        {/* Mobile logo — the branding panel is hidden on small screens */}
                        <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', justifyContent: 'center', gap: '0.75rem', mb: 2 }}>
                            <Box sx={{
                                width: 44, height: 44, bgcolor: colors.sidebarBg, borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <KlonITLogo color="white" />
                            </Box>
                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.025em', fontFamily }}>
                                KlonIT
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <Box sx={{
                                width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 1.5,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                            }}>
                                {icon}
                            </Box>
                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.25, fontFamily }}>
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>

                        {children}

                        {footer && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
                                {footer}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
}
